import React, { FC, useContext } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import SubmitButton from '../../components/submit-button/submit-button';

// Containers
import { EventFormInputs, EventRecipients, useEventForm } from './event-form';

// Helpers
import {
  getEventData,
  createSetModalContent,
  deleteEvent,
  buildEventRequest,
} from '../../helpers/event-helper';
import { recipientsToStudentIds } from '../../helpers/student-uuid-helper';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerBiggerSide,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import EventService from '../../services/event-service';
import FileStorageService from '../../services/file-storage-service';

const EventEdit: FC<{
  event: EventDto;
  classroomList: ClassroomStudents[];
  eventService: EventService;
  fileStorageService: FileStorageService;
}> = ({ event, classroomList, eventService, fileStorageService }) => {
  const navigate = useNavigate();
  const { setModalContent, setToastContent } = useContext(GeneralContext);
  const { eventId } = useParams<{ eventId: string }>();

  const form = useEventForm(() => {
    const initialData = getEventData(event);
    const { classroomIds, studentIds, unmatchedStudentUuids } = recipientsToStudentIds(
      event.recipients,
      classroomList
    );

    return {
      ...initialData,
      classData: classroomIds,
      studentData: studentIds,
      unmatchedStudentUuids,
    };
  });

  const save = (scheduleDate: string | null) =>
    form
      .submitOnce(async (current) => {
        await eventService.updateEvent(
          eventId,
          buildEventRequest({ ...current, scheduleDate }, classroomList)
        );
        setToastContent({
          isOpen: true,
          text: scheduleDate ? 'Evento agendado!' : 'Evento enviado!',
          type: 'success',
        });
        navigate('/eventos/listar');
      })
      .catch(() => {
        setToastContent({
          isOpen: true,
          text: 'Houve um erro ao salvar o evento.',
          type: 'error',
        });
      });

  return (
    <BaseContentContainer
      biggerSide={BaseContentContainerBiggerSide.right}
      format={BaseContentContainerFormat.doubleWithActionButtons}
      actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
    >
      <EventFormInputs form={form} event={event} fileStorageService={fileStorageService} />
      <EventRecipients form={form} classroomList={classroomList} />
      <ButtonsBar pattern='firstSeparate' verticalAlign='bottom'>
        <Link to='/eventos/listar'>
          <SubmitButton value='cancelar' variant='terciary' />
        </Link>
        <SubmitButton
          value='deletar'
          variant='terciary'
          onClick={() =>
            deleteEvent(
              eventService,
              eventId,
              setModalContent,
              setToastContent,
              navigate
            )
          }
        />
        <SubmitButton
          value='reagendar envio'
          variant='secondary'
          onClick={() =>
            createSetModalContent(
              form.data,
              (submitData) => save(submitData.scheduleDate),
              form.setData,
              setModalContent
            )
          }
          disabled={!form.allowSubmit}
        />
        <SubmitButton
          value='enviar'
          variant='primary'
          onClick={() => save(null)}
          disabled={!form.allowSubmit}
        />
      </ButtonsBar>
    </BaseContentContainer>
  );
};

export default EventEdit;
